/** Theme keys for pillar banners and fund cover placeholders. */
export type PillarKey = 'real_estate' | 'technology' | 'asset_lending' | string;

export const pillarLabel: Record<string, string> = {
  real_estate: 'Real Estate',
  technology: 'Technology',
  asset_lending: 'Asset Lending',
};

/** Public static covers for category headers (CRA /public). */
export const pillarImage: Record<string, string> = {
  real_estate: '/imagery/pillars/real_estate.webp',
  technology: '/imagery/pillars/technology.webp',
  asset_lending: '/imagery/pillars/asset_lending.webp',
};

export function getPillarImage(pillar: string) {
  return pillarImage[pillar] || '';
}

type Motif = {
  /** CSS background for the base plane */
  base: string;
  /** Optional SVG pattern overlay (data URI ready) */
  pattern: string;
  accent: string;
};

const pillarMotifs: Record<string, Motif> = {
  real_estate: {
    base: 'linear-gradient(135deg, #0e1528 0%, #142860 48%, #3a4254 100%)',
    accent: '#5b74b8',
    pattern: encodeSvg(`
      <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
        <g fill="none" stroke="rgba(245,246,248,0.14)" stroke-width="1">
          <path d="M10 90 L40 50 L70 90 Z"/>
          <path d="M55 90 L85 40 L110 90"/>
          <path d="M0 95 H120"/>
          <path d="M0 105 H120"/>
        </g>
      </svg>`),
  },
  technology: {
    base: 'linear-gradient(160deg, #070b16 0%, #102070 55%, #1a3678 100%)',
    accent: '#3d56a0',
    pattern: encodeSvg(`
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
        <g fill="none" stroke="rgba(245,246,248,0.16)" stroke-width="1">
          <circle cx="20" cy="20" r="2"/><circle cx="80" cy="30" r="2"/>
          <circle cx="45" cy="70" r="2"/><circle cx="70" cy="75" r="2"/>
          <path d="M20 20 L45 70 L80 30 M45 70 L70 75"/>
        </g>
      </svg>`),
  },
  asset_lending: {
    base: 'linear-gradient(120deg, #1a243c 0%, #0e1528 40%, #303030 100%)',
    accent: '#cfd3dc',
    pattern: encodeSvg(`
      <svg xmlns="http://www.w3.org/2000/svg" width="140" height="80" viewBox="0 0 140 80">
        <g fill="none" stroke="rgba(245,246,248,0.15)" stroke-width="1.2">
          <path d="M8 50 C40 20, 90 20, 132 48"/>
          <path d="M20 55 L70 40 L120 55"/>
          <path d="M55 42 L55 58 M85 42 L85 58"/>
        </g>
      </svg>`),
  },
  other: {
    base: 'linear-gradient(135deg, #0e1528 0%, #1a243c 100%)',
    accent: '#5b74b8',
    pattern: encodeSvg(`
      <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <g fill="none" stroke="rgba(245,246,248,0.12)" stroke-width="1">
          <rect x="10" y="10" width="60" height="60"/>
          <path d="M10 40 H70 M40 10 V70"/>
        </g>
      </svg>`),
  },
};

/** Per-fund tint overlays so each vehicle reads as distinct before real photos land. */
const fundTints: Record<string, string> = {
  'waco-residential':
    'linear-gradient(145deg, rgba(26,54,120,0.35), rgba(90,90,90,0.25))',
  'johnson-county':
    'linear-gradient(145deg, rgba(16,32,112,0.15), rgba(91,116,184,0.4))',
  'johnson-county-infill-lot':
    'linear-gradient(160deg, rgba(48,48,48,0.35), rgba(20,40,96,0.45))',
  'waco-cre':
    'linear-gradient(130deg, rgba(14,21,40,0.2), rgba(61,86,160,0.45))',
  'tech-ai-1':
    'linear-gradient(150deg, rgba(7,11,22,0.1), rgba(42,66,144,0.55))',
  'builder-hq-safe':
    'linear-gradient(155deg, rgba(7,11,22,0.15), rgba(26,54,120,0.5))',
  'equilibria-safe':
    'linear-gradient(145deg, rgba(16,32,112,0.2), rgba(61,86,160,0.45))',
  'aircraft-lending-i':
    'linear-gradient(140deg, rgba(26,36,60,0.15), rgba(207,211,220,0.22))',
  'aircraft-lending-ii':
    'linear-gradient(140deg, rgba(7,11,22,0.35), rgba(67,69,72,0.35))',
};

function encodeSvg(svg: string) {
  return `url("data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}")`;
}

export function getPillarMotif(pillar: string): Motif {
  return pillarMotifs[pillar] || pillarMotifs.other;
}

export function getFundTint(slug: string) {
  return fundTints[slug] || 'linear-gradient(145deg, rgba(16,32,112,0.2), transparent)';
}
